// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { productPhotoMetadata } from '@iheartjane/menu';
import {
  Typography,
  getResponsiveImageAttributes,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import type { ProductCategory } from '@iheartjane/types';
import type { HTMLAttributes, PropsWithChildren, ReactNode } from 'react';

/* eslint-disable-next-line no-restricted-imports */
import type { MenuProduct, Product } from '@jane/shared/models';

export const Brand = ({
  children,
  color: _,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <Typography
    color="text-light"
    truncateAt="auto"
    variant="body"
    {...props}
    as="div"
  >
    {children}
  </Typography>
);

const getCategoryColor = (category: string) => {
  switch (category) {
    case 'indica':
      return 'purple';
    case 'sativa':
      return 'emerald';
    case 'cbd':
      return 'sunset';
    case 'hybrid':
      return 'grays-black';
    default:
      return 'grays-white';
  }
};

export const Category = ({
  color: _,
  children,
  category,
  editingMode,
  ...props
}: PropsWithChildren<
  HTMLAttributes<HTMLDivElement> & {
    category?: ProductCategory | null;
    editingMode?: boolean;
  }
>) => {
  const color = getCategoryColor((category || '').toLowerCase());

  return (
    <Typography
      as="div"
      color={editingMode ? 'grays-white' : color}
      variant="mini-bold"
      {...props}
    >
      {children}
    </Typography>
  );
};

export const Kind = ({
  children,
  color: _,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <Typography
    as="div"
    color="text-light"
    truncateAt="auto"
    variant="body"
    {...props}
  >
    {children}
  </Typography>
);

const StyledTypography = styled(Typography)({
  '@supports (-webkit-line-clamp: 2)': {
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    overflow: 'hidden',
  },
});

export const Name = ({
  children,
  color: _,
  ...props
}: PropsWithChildren<HTMLAttributes<HTMLDivElement>>) => (
  <StyledTypography as="div" variant="body-bold" {...props}>
    {children}
  </StyledTypography>
);

export const StrikeThroughPrice = ({ children }: { children: ReactNode }) => (
  <Typography color="text-light" strikeThrough variant="body">
    {children}
  </Typography>
);

export const useImage = (product?: Product | MenuProduct | any) => {
  const isMobile = useMobileMediaQuery({});

  const photoMetadata = product
    ? productPhotoMetadata(product)
    : { photoSource: 'jane', url: '' };

  // TODO: reefer image can generate srcset with `responsive` prop, this can be removed/refactored
  const { srcSet, imageSrc } = getResponsiveImageAttributes({
    src: photoMetadata.url,
    fallbackWidth: 200,
  });

  const imageDimension = isMobile ? '150px' : '200px';

  return {
    srcSet,
    imageSrc,
    imageDimension,
  };
};
